import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useUpdateProfile } from "@/api/use-customer-profile-api";
import type { AxiosError } from "axios";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { HeroFormField, HeroInputField } from "@/components/hero-form";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";
import type { Customer } from "@/api.types";

type ProfileFormValues = {
  firstName: string;
  lastName: string;
  phone?: string;
};

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer;
}

export function EditProfileDialog({
  open,
  onOpenChange,
  customer,
}: EditProfileDialogProps) {
  const { t } = useTranslation("app");
  const { toast } = useToast();
  const profileSchema = z.object({
    firstName: z.string().min(1, t("validation.firstNameRequired")),
    lastName: z.string().min(1, t("validation.lastNameRequired")),
    phone: z.string().optional(),
  });

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: customer?.firstName ?? "",
      lastName: customer?.lastName ?? "",
      phone: customer?.phone ?? "",
    },
  });

  const updateProfileMutation = useUpdateProfile({
    onSuccess: () => {
      onOpenChange(false);
      toast({
        title: t("profileScreen.toast.success"),
        description: t("profileScreen.toast.updated"),
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast({
        title: t("profileScreen.toast.error"),
        description:
          error.response?.data?.message ??
          t("profileScreen.toast.updateFailed"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    updateProfileMutation.mutate(values);
  });

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      disableAnimation
      size="md"
    >
      <ModalContent>
        {(onClose) => (
          <form onSubmit={onSubmit}>
            <ModalHeader className="flex-col items-start gap-1">
              <span>{t("settings.editProfile")}</span>
              <span className="text-small text-default-500 font-normal">
                {t("profileScreen.editDialogDescription")}
              </span>
            </ModalHeader>
            <ModalBody className="gap-4">
              <HeroInputField
                control={form.control}
                name="firstName"
                label={t("onboarding.firstName")}
                variant="flat"
              />
              <HeroInputField
                control={form.control}
                name="lastName"
                label={t("onboarding.lastName")}
                variant="flat"
              />
              <Input
                label={t("settings.email")}
                value={customer.email}
                isDisabled
                variant="flat"
              />
              <HeroFormField
                control={form.control}
                name="phone"
                render={({ field, fieldState }) => (
                  <div>
                    <label className="block text-tiny font-medium text-default-700 mb-1.5">
                      {t("settings.phone")}
                    </label>
                    <PhoneInputShadcn
                      value={field.value ?? ""}
                      onChange={(v) => field.onChange(v ?? "")}
                      onBlur={field.onBlur}
                      defaultCountry="CA"
                    />
                    {fieldState.error?.message && (
                      <p className="mt-1 text-tiny text-danger">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                {t("actions.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                isDisabled={updateProfileMutation.isPending}
                isLoading={updateProfileMutation.isPending}
              >
                {updateProfileMutation.isPending
                  ? t("actions.saving")
                  : t("actions.saveChanges")}
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
}
